import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { toDateParam, parseDateOnly } from '@/lib/date';
import { Badge } from '@/components/ui/badge';
import { useDepartments, useLocations, useSeniorityLevels, useEmploymentTypes } from '@/hooks/use-organizations';
import { useProjects } from '@/hooks/use-projects';
import { useSearchSkills } from '@/hooks/use-recruitment';
import { normalizeSkillName } from '@/lib/skills';
import type { VacancyDetail, VacancyPriority, SalaryCurrency, UpdateVacancyInput } from '@/types/recruitment';
import { X, Lock } from 'lucide-react';
import { useRef, useState, type RefObject } from 'react';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { z } from 'zod';
import { EmployeeMultiSelect, type EmployeeOption } from './employee-multi-select';

// Mirrors backend `@IsUrl()` (class-validator) defaults: empty is allowed (field is optional);
// otherwise requires http(s) scheme and a host with a TLD, so values like `https://hygge-s`
// are rejected client-side before the PATCH is sent.
const jiraLinkSchema = z.literal('').or(
  z
    .string()
    .url('Enter a valid URL')
    .refine((v) => {
      try {
        const url = new URL(v);
        return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
      } catch {
        return false;
      }
    }, 'Enter a complete URL (e.g. https://jira.example.com/browse/ABC-1)'),
);

function validateJiraLink(value: string): string | null {
  const result = jiraLinkSchema.safeParse(value.trim());
  return result.success ? null : (result.error.issues[0]?.message ?? 'Invalid URL');
}

// A rendered but empty salary input is a deliberate "no bound" and sends null, which clears
// the column — sending `undefined` there is what made clearing a salary impossible and hid
// inverted ranges from both the client check and the server guard (HW-770). A null ref means
// the block never rendered, and the bound must then stay out of the payload entirely.
//
// An entry the browser cannot parse ("$5,000", "5000e") is a third case that looks exactly like
// the second: `<input type="number">` reports it as an empty `value` and raises
// `validity.badInput`. Without that flag a typo would read as "cleared" and delete a stored
// bound, while the rejected text sits visibly in the box — so it is surfaced as an error.
const INVALID_SALARY_INPUT = Symbol('invalid salary input');

function readSalaryInput(
  ref: RefObject<HTMLInputElement | null>,
): number | null | undefined | typeof INVALID_SALARY_INPUT {
  if (!ref.current) return undefined;
  if (ref.current.validity.badInput) return INVALID_SALARY_INPUT;
  return ref.current.value ? Number(ref.current.value) : null;
}

// A value the browser accepts is not necessarily one the server does: it takes a non-negative
// integer (@IsInt/@Min(0)), while `min={0}` on the input is inert because this form is submitted
// by button rather than natively. Without this check a fraction or a negative reaches the API
// and returns as a bare toast — the response-to-field mapping below only covers jiraLink — and
// the inline slot next to the offending input stays empty.
function isInvalidBound(value: number | null | undefined): boolean {
  return value != null && (!Number.isInteger(value) || value < 0);
}

function extractServerMessages(err: unknown): string[] {
  if (!isAxiosError(err) || !err.response?.data || typeof err.response.data !== 'object') return [];
  const body = err.response.data as { message?: unknown };
  if (Array.isArray(body.message)) return body.message.filter((m): m is string => typeof m === 'string');
  if (typeof body.message === 'string') return [body.message];
  return [];
}

interface VacancyDetailsTabProps {
  vacancy: VacancyDetail;
  onSave: (data: UpdateVacancyInput) => Promise<void> | void;
  onCancel: () => void;
  isSaving: boolean;
}

export function VacancyDetailsTab({ vacancy, onSave, onCancel, isSaving }: VacancyDetailsTabProps) {
  // Whether this editor deals with the salary at all is read off the response, not re-derived
  // from a permission code: `GET /vacancies/:id` omits the three salary keys entirely for
  // callers who may not see them, so their presence is the server's own answer to the same
  // question. Checking the permission code here gave a second, independently-computed answer,
  // and the two disagree: this matcher and the canonical PermissionsGuard both accept
  // `recruitment.vacancies.*`, while the read gate hand-rolled in vacancies.controller.ts does
  // not — it is the outlier, and reconciling it belongs to HW-780. Until then an editor holding
  // that code was shown empty salary inputs over a populated range, and whatever they typed
  // there overwrote it unseen (HW-770).
  const canSeeSalary = 'salaryMin' in vacancy;

  // Reference data
  const { data: departments = [] } = useDepartments();
  const { data: locations = [] } = useLocations();
  const { data: seniorityLevels = [] } = useSeniorityLevels();
  const { data: employmentTypes = [] } = useEmploymentTypes();
  const { data: projectsData } = useProjects({ limit: 100 });
  const projects = projectsData?.data ?? [];

  // Refs for text inputs
  const titleRef = useRef<HTMLInputElement>(null);
  const jiraLinkRef = useRef<HTMLInputElement>(null);
  const salaryMinRef = useRef<HTMLInputElement>(null);
  const salaryMaxRef = useRef<HTMLInputElement>(null);
  const openingsRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Controlled state
  const [priority, setPriority] = useState<VacancyPriority>(vacancy.priority);
  const [departmentId, setDepartmentId] = useState(vacancy.department?.id ?? '');
  const [locationId, setLocationId] = useState(vacancy.location?.id ?? '');
  const [employmentTypeId, setEmploymentTypeId] = useState(vacancy.employmentType?.id ?? '');
  const [seniorityLevelId, setSeniorityLevelId] = useState(vacancy.seniorityLevel?.id ?? '');
  const [projectId, setProjectId] = useState(vacancy.project?.id ?? '');
  const [salaryCurrency, setSalaryCurrency] = useState<SalaryCurrency>(vacancy.salaryCurrency ?? 'USD');
  const [recruitingFrom, setRecruitingFrom] = useState<Date | null>(
    vacancy.recruitingFrom ? parseDateOnly(vacancy.recruitingFrom) : null,
  );
  const [recruitingTo, setRecruitingTo] = useState<Date | null>(
    vacancy.recruitingTo ? parseDateOnly(vacancy.recruitingTo) : null,
  );

  // Skills
  const [skills, setSkills] = useState<string[]>(vacancy.skills.map((s) => s.name));
  const [skillInput, setSkillInput] = useState('');
  const { data: skillSuggestions = [] } = useSearchSkills(skillInput);

  // Tags
  const [tags, setTags] = useState<string[]>(vacancy.tags.map((t) => t.name));
  const [tagInput, setTagInput] = useState('');

  // Recruiters
  const [recruiters, setRecruiters] = useState<EmployeeOption[]>(
    vacancy.members.filter((m) => m.role === 'RECRUITER').map((m) => m.employee),
  );

  // Hiring Team
  const [hiringTeam, setHiringTeam] = useState<EmployeeOption[]>(
    vacancy.members.filter((m) => m.role === 'HIRING_TEAM').map((m) => m.employee),
  );

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const title = titleRef.current?.value.trim() ?? '';
    const jiraLink = jiraLinkRef.current?.value.trim() ?? '';
    const salaryMinInput = readSalaryInput(salaryMinRef);
    const salaryMaxInput = readSalaryInput(salaryMaxRef);
    const openings = openingsRef.current?.value ? Number(openingsRef.current.value) : 1;
    const comment = commentRef.current?.value.trim() ?? '';

    // Validate
    const newErrors: Record<string, string> = {};
    if (!title) newErrors.title = 'Title is required';
    if (recruiters.length === 0) newErrors.recruiters = 'At least one recruiter is required';
    const salaryMin = salaryMinInput === INVALID_SALARY_INPUT ? undefined : salaryMinInput;
    const salaryMax = salaryMaxInput === INVALID_SALARY_INPUT ? undefined : salaryMaxInput;
    if (salaryMinInput === INVALID_SALARY_INPUT || isInvalidBound(salaryMin)) {
      newErrors.salaryMin = 'Enter a whole number, 0 or more';
    }
    if (salaryMaxInput === INVALID_SALARY_INPUT || isInvalidBound(salaryMax)) {
      newErrors.salaryMax = 'Enter a whole number, 0 or more';
    }
    if (salaryMin != null && salaryMax != null && salaryMax < salaryMin) {
      newErrors.salaryMax = 'Must be >= salary min';
    }
    const jiraLinkErr = validateJiraLink(jiraLink);
    if (jiraLinkErr) newErrors.jiraLink = jiraLinkErr;
    if (recruitingFrom && recruitingTo && recruitingTo < recruitingFrom) {
      newErrors.recruitingTo = 'Must be on or after Recruiting From';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      await onSave({
        title,
        priority,
        departmentId: departmentId || undefined,
        locationId: locationId || undefined,
        employmentTypeId: employmentTypeId || undefined,
        seniorityLevelId: seniorityLevelId || undefined,
        projectId: projectId || undefined,
        jiraLink: jiraLink || undefined,
        // Salary is written as a unit — both bounds plus the currency they are expressed in.
        // Clearing both bounds clears the currency too, so no orphan currency is left behind
        // on a vacancy that no longer states a range.
        ...(canSeeSalary && {
          salaryMin,
          salaryMax,
          salaryCurrency: salaryMin != null || salaryMax != null ? salaryCurrency : null,
        }),
        openings,
        recruitingFrom: toDateParam(recruitingFrom) ?? undefined,
        recruitingTo: toDateParam(recruitingTo) ?? undefined,
        comment: comment || undefined,
        skills: skills.length ? skills : undefined,
        tags: tags.length ? tags : undefined,
        recruiterIds: recruiters.map((r) => r.id),
        hiringTeamIds: hiringTeam.map((h) => h.id),
      });
    } catch (err) {
      // Surface server-side validation errors that slipped past the FE checks.
      // Backend returns `{ message: string | string[] }` from class-validator; map known fields
      // to inline errors and always raise a toast so the failure is impossible to miss.
      const messages = extractServerMessages(err);
      const fieldErrors: Record<string, string> = {};
      for (const msg of messages) {
        if (msg.toLowerCase().includes('jiralink')) fieldErrors.jiraLink = msg;
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      toast.error(messages[0] ?? 'Failed to save vacancy');
    }
  };

  const addSkill = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      setSkillInput('');
      return;
    }
    // Case-insensitive de-dup so "React" + "react" don't both end up in the payload (HW-445).
    const normalized = normalizeSkillName(trimmed);
    const isDuplicate = skills.some((s) => normalizeSkillName(s) === normalized);
    if (!isDuplicate) {
      setSkills([...skills, trimmed]);
    }
    setSkillInput('');
  };

  const addTag = (name: string) => {
    const trimmed = name.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          ref={titleRef}
          id="title"
          defaultValue={vacancy.title}
          placeholder="e.g. Senior Frontend Developer"
          maxLength={200}
          aria-invalid={!!errors.title}
        />
        {errors.title && <p className="text-sm text-destructive mt-1">{errors.title}</p>}
      </div>

      {/* Department + Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Department</Label>
          <SearchableSelect
            value={departmentId}
            onChange={setDepartmentId}
            options={departments}
            ariaInvalid={!!errors.departmentId}
          />
          {errors.departmentId && <p className="text-sm text-destructive mt-1">{errors.departmentId}</p>}
        </div>
        <div>
          <Label>Priority</Label>
          <Select value={priority} onValueChange={(v) => setPriority(v as VacancyPriority)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="LOW">Low</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="CRITICAL">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location + Employment Type */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Location</Label>
          <SearchableSelect
            value={locationId}
            onChange={setLocationId}
            options={locations}
            ariaInvalid={!!errors.locationId}
          />
          {errors.locationId && <p className="text-sm text-destructive mt-1">{errors.locationId}</p>}
        </div>
        <div>
          <Label>Employment Type</Label>
          <Select value={employmentTypeId || '__none__'} onValueChange={(v) => setEmploymentTypeId(v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {employmentTypes.map((t) => (
                <SelectItem
                  key={t.id}
                  value={t.id}
                  className="[&>span:last-child]:truncate"
                  title={t.name}
                >
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Seniority + Project */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Seniority Level</Label>
          <Select value={seniorityLevelId || '__none__'} onValueChange={(v) => setSeniorityLevelId(v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {seniorityLevels.map((s) => (
                <SelectItem
                  key={s.id}
                  value={s.id}
                  className="[&>span:last-child]:truncate"
                  title={s.name}
                >
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Project</Label>
          <Select value={projectId || '__none__'} onValueChange={(v) => setProjectId(v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {projects.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  className="[&>span:last-child]:truncate"
                  title={p.name}
                >
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Recruiting Period + Openings */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Recruiting From</Label>
          <DatePicker
            value={recruitingFrom}
            onChange={setRecruitingFrom}
            maxDate={recruitingTo ?? undefined}
          />
        </div>
        <div>
          <Label>Recruiting To</Label>
          <DatePicker
            value={recruitingTo}
            onChange={setRecruitingTo}
            minDate={recruitingFrom ?? undefined}
          />
          {errors.recruitingTo && (
            <p className="text-sm text-destructive mt-1">{errors.recruitingTo}</p>
          )}
        </div>
        <div>
          <Label>Openings</Label>
          <Input
            ref={openingsRef}
            type="number"
            min={1}
            defaultValue={vacancy.openings}
          />
        </div>
      </div>

      {/* Salary Range */}
      {canSeeSalary && (
        <div>
          <Label className="flex items-center gap-2">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            Salary Range
          </Label>
          <div className="grid grid-cols-3 gap-4 mt-1">
            <div>
              <Input
                ref={salaryMinRef}
                type="number"
                min={0}
                placeholder="Min"
                defaultValue={vacancy.salaryMin ?? ''}
                aria-invalid={!!errors.salaryMin}
              />
              {errors.salaryMin && <p className="text-sm text-destructive mt-1">{errors.salaryMin}</p>}
            </div>
            <div>
              <Input
                ref={salaryMaxRef}
                type="number"
                min={0}
                placeholder="Max"
                defaultValue={vacancy.salaryMax ?? ''}
                aria-invalid={!!errors.salaryMax}
              />
              {errors.salaryMax && <p className="text-sm text-destructive mt-1">{errors.salaryMax}</p>}
            </div>
            <div>
              <Select value={salaryCurrency} onValueChange={(v) => setSalaryCurrency(v as SalaryCurrency)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="UAH">UAH</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Required Skills */}
      <div>
        <Label>Required Skills</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {skills.map((s) => (
            <Badge key={s} variant="secondary" className="max-w-[200px] gap-1" title={s}>
              <span className="truncate">{s}</span>
              <X
                className="h-3 w-3 shrink-0 cursor-pointer"
                onClick={() => setSkills(skills.filter((sk) => sk !== s))}
              />
            </Badge>
          ))}
        </div>
        <div className="relative">
          <Input
            placeholder="Type to add skill..."
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addSkill(skillInput); }
            }}
          />
          {skillSuggestions.length > 0 && skillInput.length >= 2 && (
            <div className="absolute z-10 top-full mt-1 w-full bg-popover border rounded-md shadow-md max-h-40 overflow-auto">
              {skillSuggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="w-full truncate px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => addSkill(s.name)}
                  title={s.name}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      <div>
        <Label>Tags</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((t) => (
            <Badge key={t} variant="outline" className="max-w-[200px] gap-1" title={t}>
              <span className="truncate">{t}</span>
              <X
                className="h-3 w-3 shrink-0 cursor-pointer"
                onClick={() => setTags(tags.filter((tg) => tg !== t))}
              />
            </Badge>
          ))}
        </div>
        <Input
          placeholder="Type and press Enter..."
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); }
          }}
        />
      </div>

      {/* Jira Link */}
      <div>
        <Label htmlFor="jiraLink">Jira Link</Label>
        <Input
          ref={jiraLinkRef}
          id="jiraLink"
          type="url"
          defaultValue={vacancy.jiraLink ?? ''}
          placeholder="https://jira.example.com/browse/..."
          aria-invalid={!!errors.jiraLink}
          onBlur={(e) => {
            const err = validateJiraLink(e.target.value);
            setErrors((prev) => {
              const next = { ...prev };
              if (err) next.jiraLink = err;
              else delete next.jiraLink;
              return next;
            });
          }}
          onChange={() => {
            if (errors.jiraLink) {
              setErrors((prev) => {
                const next = { ...prev };
                delete next.jiraLink;
                return next;
              });
            }
          }}
        />
        {errors.jiraLink && <p className="text-sm text-destructive mt-1">{errors.jiraLink}</p>}
      </div>

      {/* Responsible Recruiter */}
      <div>
        <Label>Responsible Recruiter *</Label>
        <EmployeeMultiSelect
          value={recruiters}
          onChange={setRecruiters}
          excludeIds={hiringTeam.map((h) => h.id)}
          ariaInvalid={!!errors.recruiters}
        />
        {errors.recruiters && <p className="text-sm text-destructive mt-1">{errors.recruiters}</p>}
      </div>

      {/* Hiring Team */}
      <div>
        <Label>Hiring Team (Managers & Interviewers)</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Read-only access to vacancy details. Can view and comment on candidates.
        </p>
        <EmployeeMultiSelect
          value={hiringTeam}
          onChange={setHiringTeam}
          excludeIds={recruiters.map((r) => r.id)}
        />
      </div>

      {/* Comment */}
      <div>
        <Label htmlFor="comment">Internal Comment</Label>
        <Textarea
          ref={commentRef}
          id="comment"
          defaultValue={vacancy.comment ?? ''}
          placeholder="Internal notes..."
          rows={3}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="accent"
          onClick={() => handleSubmit()}
          disabled={isSaving || !!errors.jiraLink}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
